"use client";

import { useRouter } from "@commerce-frontend/i18n/navigation";
import type { Locale } from "@commerce-frontend/i18n/types";
import type { LineItemFragment } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import {
	ModalClose,
	ModalContent,
	ModalHeader,
	ModalMain,
	ModalOverlay,
	ModalPortal,
	ModalRoot,
	ModalTitle,
} from "@commerce-frontend/ui/components/Modal/Modal";
import { isValue } from "@labdigital/toolkit";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { useAddToCartContext } from "~/components/AddToCartContext";
import { LineItemsOverview } from "~/components/LineItemsOverview/LineItemsOverview";

type Props = {
	locale: Locale;
	onAddVariant: (sku: string) => Promise<LineItemFragment | undefined>;
};

export function ProductAddedToCartModal({ locale }: Props) {
	const t = useTranslations("ProductAddedToCartModal");
	const { addedLineItem, setAddedLineItem } = useAddToCartContext();
	const router = useRouter();

	const goToCart = useMemo(
		() => () => router.push({ pathname: "/cart" }, { locale }),
		[router, locale],
	);

	return (
		<ModalRoot open={Boolean(addedLineItem)} onOpenChange={() => setAddedLineItem(undefined)}>
			<ModalPortal>
				<ModalOverlay />
				<ModalContent size="small" alignment="topRight">
					<ModalTitle>{t("title")}</ModalTitle>
					<div className="flex size-8 items-center justify-center rounded-full bg-success-100 p-md">
						<Icon icon="check" className="size-4 bg-success-600" />
					</div>
					<ModalMain>
						<ModalHeader>
							<h3 className="mb-xl text-md font-semibold">{t("title")}</h3>

							<ModalClose asChild>
								<Button
									className="absolute focus:ring-accent-level-2 group -right-2.5 -top-2.5 size-11 rounded-xs text-gray-400 hover:bg-gray-50 hover:text-gray-500 focus:bg-gray-50"
									variant="link"
									aria-label={t("close")}
								>
									<Icon
										icon="x-close"
										className="size-6 bg-gray-400 transition-colors group-hover:bg-gray-700"
									/>
								</Button>
							</ModalClose>
						</ModalHeader>
						<div className="flex flex-col gap-xl">
							<LineItemsOverview lineItems={[addedLineItem].filter(isValue)} />

							<div className="flex w-full shrink-0 flex-col gap-lg lg:flex-row">
								<Button
									className="order-2 w-full lg:order-none lg:w-1/2"
									variant="secondary"
									onClick={() => setAddedLineItem(undefined)}
								>
									{t("continue-shopping")}
								</Button>
								<Button
									className="order-1 w-full lg:order-none lg:w-1/2"
									variant="secondaryColor"
									onClick={goToCart}
								>
									{t("view-my-cart")}
								</Button>
							</div>
						</div>
					</ModalMain>
				</ModalContent>
			</ModalPortal>
		</ModalRoot>
	);
}
